import { useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';

import { usePostHogDistinctId } from './usePostHogDistinctId';

interface UseAdoptPostHogIdentityOptions {
  /**
   * Set to false on a screen that redirects away on mount. It then reads no
   * partner param, starts no decode, and leaves identity alone — the screen it
   * redirects to does all three.
   */
  enabled?: boolean;
  /**
   * Set to true when the screen that navigated here already settled identity for
   * this activation. reset() clears super properties and starts a new session, so
   * a second one mid-activation drops the flow properties the previous screen
   * registered and splits the funnel in two.
   */
  skipReset?: boolean;
  /**
   * Runs once, immediately after the identify-or-reset decision. Put a screen's
   * landing captures here: fired before the decision they would go out under
   * whoever the browser was identified as last (PAT-1117).
   */
  onReady?: () => void;
}

interface UseAdoptPostHogIdentityResult {
  /** True once the identify-or-reset decision has been applied on this screen. */
  ready: boolean;
  distinctId: string | undefined;
  loading: boolean;
}

/**
 * Adopts a partner's PostHog identity on an entry screen, or clears the previous
 * person's if there is none.
 *
 * Every screen that starts an enrollment has to make the same decision in the same
 * order, and the order is what matters: a partner deep link (a Xealth order, the
 * Spark app's login handoff) passes its identity as ph_distinct_id or ph_token, and
 * that identity must be adopted rather than wiped; with no partner identity, the
 * browser must be reset so the last person to enroll on this device is not credited
 * with this session's events; and nothing may be captured until one of the two has
 * happened. Keeping it in one hook is what stops the four entry screens from
 * drifting apart on any of those three points.
 */
export const useAdoptPostHogIdentity = ({
  enabled = true,
  skipReset = false,
  onReady,
}: UseAdoptPostHogIdentityOptions = {}): UseAdoptPostHogIdentityResult => {
  const posthog = usePostHog();
  const { distinctId, loading } = usePostHogDistinctId({ enabled });
  const [ready, setReady] = useState(false);
  const appliedRef = useRef(false);

  // Held in a ref so an inline callback doesn't have to be memoised by every
  // caller to keep the effect below from re-running.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    // Wait for a ph_token to finish decoding before deciding: resetting mid-decode
    // would erase the identity about to be adopted.
    if (!enabled || !posthog || loading || appliedRef.current) {
      return;
    }

    appliedRef.current = true;

    if (distinctId) {
      posthog.identify(distinctId);
    } else if (!skipReset) {
      posthog.reset();
    }

    setReady(true);
    onReadyRef.current?.();
  }, [posthog, distinctId, loading, enabled, skipReset]);

  return { ready, distinctId, loading };
};
